import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Loader2, Check, Save, RefreshCw, ChevronRight } from 'lucide-react';

import { QuizHeader } from './kbquiz/QuizHeader';
import { ChatMessages } from './kbquiz/ChatMessages';
import { KBReviewForm } from './kbquiz/KBReviewForm';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// ── Main Modal ─────────────────────────────────────────────────────────────────
export const KBQuizModal = ({ token, domainId, callType, categoryName, onSaved, onClose }) => {
  const headers = { Authorization: `Bearer ${token}` };
  const bottomRef = useRef(null);

  // Phase: 'starting' | 'questions' | 'generating' | 'review' | 'saving' | 'done'
  const [phase, setPhase] = useState('starting');
  const [sessionId, setSessionId] = useState(null);
  const [totalQuestions, setTotalQuestions] = useState(0);
  const [currentIdx, setCurrentIdx] = useState(0);
  const [answer, setAnswer] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [messages, setMessages] = useState([]);   // chat history for display
  const [editedCriteria, setEditedCriteria] = useState([]);
  const [editedName, setEditedName] = useState('');
  const [editedDesc, setEditedDesc] = useState('');
  const [editedThreshold, setEditedThreshold] = useState(70);

  // Start the session on mount
  useEffect(() => {
    (async () => {
      try {
        const r = await axios.post(`${API}/kb-quiz/start`, { domain_id: domainId, call_type: callType, category_name: categoryName }, { headers });
        setSessionId(r.data.session_id);
        setTotalQuestions(r.data.total_questions);
        setCurrentIdx(0);
        setMessages([{ role: 'ai', text: r.data.question }]);
        setPhase('questions');
      } catch (e) {
        toast.error('Failed to start AI Quiz');
        onClose();
      }
    })();
   
  }, []);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, phase]);

  const generateKB = async () => {
    setPhase('generating');
    try {
      const r = await axios.post(`${API}/kb-quiz/${sessionId}/generate`, {}, { headers });
      const kb = r.data.generated_kb;
      setEditedName(kb.name || '');
      setEditedDesc(kb.description || '');
      setEditedThreshold(kb.threshold_score || 70);
      setEditedCriteria((kb.criteria || []).map(c => ({ description: c.description, weight: c.weight || 1 })));
      setPhase('review');
    } catch (e) {
      toast.error('AI generation failed — please try again');
      setPhase('questions');
    }
  };

  const submitAnswer = async () => {
    if (!answer.trim()) return;
    setSubmitting(true);
    const myAnswer = answer.trim();
    setAnswer('');
    setMessages(m => [...m, { role: 'user', text: myAnswer }]);

    try {
      const r = await axios.post(`${API}/kb-quiz/${sessionId}/answer`, { answer: myAnswer }, { headers });
      if (r.data.is_complete) {
        setMessages(m => [...m, { role: 'ai', text: "All done! Generating your Knowledge Base now..." }]);
        setPhase('generating');
        await generateKB();
      } else {
        setCurrentIdx(r.data.current_question_index);
        setMessages(m => [...m, { role: 'ai', text: r.data.question }]);
        setPhase('questions');
      }
    } catch {
      toast.error('Failed to submit answer');
    } finally {
      setSubmitting(false);
    }
  };

  const addCriterion = () => setEditedCriteria(c => [...c, { description: '', weight: 2 }]);
  const updateCriterion = (i, k, v) => setEditedCriteria(c => { const n = [...c]; n[i] = { ...n[i], [k]: v }; return n; });
  const removeCriterion = i => setEditedCriteria(c => c.filter((_, idx) => idx !== i));

  const saveKB = async () => {
    if (!editedName.trim()) { toast.error('KB name is required'); return; }
    setPhase('saving');
    try {
      await axios.post(`${API}/kb-quiz/${sessionId}/save`, {
        kb: {
          name: editedName,
          description: editedDesc,
          threshold_score: editedThreshold,
          criteria: editedCriteria.filter(c => c.description.trim()),
        },
      }, { headers });
      toast.success(`"${editedName}" saved successfully`);
      setPhase('done');
      onSaved();
    } catch {
      toast.error('Failed to save KB');
      setPhase('review');
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) submitAnswer();
  };

  const isChatPhase = phase === 'questions' || phase === 'generating';
  const isReviewPhase = phase === 'review' || phase === 'saving' || phase === 'done';

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: 'rgba(0,0,0,0.75)' }}>
      <div className="bg-[#0D1426] border border-white/10 rounded-2xl w-full max-w-2xl max-h-[90vh] flex flex-col shadow-2xl">
        <QuizHeader
          categoryName={categoryName}
          phase={phase}
          currentIdx={currentIdx}
          totalQuestions={totalQuestions}
          onClose={onClose}
        />

        <div className="flex-1 overflow-y-auto">
          {phase === 'starting' && (
            <div className="flex flex-col items-center justify-center py-16 gap-3">
              <Loader2 className="w-8 h-8 text-teal-400 animate-spin" />
              <p className="text-white/40 text-sm">Preparing your coaching session...</p>
            </div>
          )}

          {isChatPhase && (
            <ChatMessages
              messages={messages}
              isGenerating={phase === 'generating'}
              bottomRef={bottomRef}
            />
          )}

          {isReviewPhase && (
            <KBReviewForm
              editedName={editedName} setEditedName={setEditedName}
              editedDesc={editedDesc} setEditedDesc={setEditedDesc}
              editedThreshold={editedThreshold} setEditedThreshold={setEditedThreshold}
              editedCriteria={editedCriteria}
              addCriterion={addCriterion}
              updateCriterion={updateCriterion}
              removeCriterion={removeCriterion}
              isDone={phase === 'done'}
            />
          )}
        </div>

        {/* Footer actions */}
        <div className="flex items-center gap-2 px-6 py-4 border-t border-white/10 flex-shrink-0">
          {phase === 'questions' && (
            <>
              <textarea
                value={answer}
                onChange={e => setAnswer(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder="Type your answer... (Ctrl+Enter to submit)"
                rows={2}
                className="flex-1 bg-white/5 border border-white/10 rounded-xl px-4 py-2.5 text-white text-sm placeholder-white/20 focus:outline-none focus:border-teal-500/50 resize-none"
              />
              <button
                onClick={submitAnswer}
                disabled={!answer.trim() || submitting}
                className="flex items-center gap-2 px-4 py-2.5 bg-teal-500 hover:bg-teal-400 text-white text-sm font-medium rounded-xl disabled:opacity-40 transition-colors flex-shrink-0"
                data-testid="quiz-submit-answer"
              >
                {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <ChevronRight className="w-4 h-4" />}
              </button>
            </>
          )}

          {phase === 'review' && (
            <>
              <button onClick={onClose} className="px-4 py-2 bg-white/5 hover:bg-white/10 border border-white/10 text-white/50 text-sm rounded-xl">
                Cancel
              </button>
              <button
                onClick={generateKB}
                className="flex items-center gap-1.5 px-3 py-2 bg-white/5 hover:bg-white/10 border border-white/10 text-white/50 text-sm rounded-xl"
                title="Regenerate"
              >
                <RefreshCw className="w-3.5 h-3.5" />Regenerate
              </button>
              <button
                onClick={saveKB}
                className="flex items-center gap-2 px-4 py-2 bg-teal-500 hover:bg-teal-400 text-white text-sm font-medium rounded-xl ml-auto"
                data-testid="quiz-save-kb"
              >
                <Save className="w-4 h-4" />Save to Domain
              </button>
            </>
          )}

          {phase === 'saving' && (
            <div className="flex items-center gap-2 text-white/40 text-sm mx-auto">
              <Loader2 className="w-4 h-4 animate-spin text-teal-400" />Saving...
            </div>
          )}

          {phase === 'done' && (
            <button
              onClick={onClose}
              className="flex items-center gap-2 px-4 py-2 bg-teal-500 hover:bg-teal-400 text-white text-sm font-medium rounded-xl mx-auto"
            >
              <Check className="w-4 h-4" />Done
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default KBQuizModal;
